import { AvatarFragment$key } from '@/__generated__/AvatarFragment.graphql'
import { DEFAULT_AVATAR_SRC } from '@ourzora/utils'
import { useCallback, useState } from 'react'
import { Blurhash } from 'react-blurhash'
import { graphql, useFragment } from 'react-relay'
import { Responsive, View } from 'reshaped'

const fragment = graphql`
  fragment AvatarFragment on IGraphQLProfile {
    handle
    avatar {
      icon
      small
      blurhash
    }
  }
`

export function Avatar({
  avatarKey,
  size,
}: {
  avatarKey: AvatarFragment$key
  size: Responsive<string | number>
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const { handle, avatar } = useFragment(fragment, avatarKey)

  const handleLoad = useCallback(() => {
    setIsLoading(false)
    setHasError(false)
  }, [])

  const handleError = useCallback(() => {
    setHasError(true)
    setIsLoading(false)
  }, [])

  const showFallback = hasError || (isLoading && !avatar?.blurhash) || !avatar?.small

  return (
    <View
      align="center"
      justify="center"
      position="relative"
      overflow="hidden"
      borderRadius="circular"
      width={size}
      height={size}
    >
      {avatar?.small && (
        <img
          src={avatar?.small}
          alt={handle ?? undefined}
          width="100%"
          height="100%"
          loading="lazy"
          fetchPriority="auto"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      {isLoading && avatar?.blurhash && (
        <View position="absolute" inset={0}>
          <Blurhash hash={avatar?.blurhash} width="100%" height="100%" />
        </View>
      )}
      {showFallback && (
        <View
          position="absolute"
          inset={0}
          backgroundColor="page"
          width={size}
          height={size}
        >
          <img
            src={DEFAULT_AVATAR_SRC}
            alt={handle ?? undefined}
            width="100%"
            height="100%"
          />
        </View>
      )}
    </View>
  )
}
